import React, { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WidgetWrapper } from '../../../../components/WidgetWrapper/WidgetWrapper';
import { EditField } from '../../../../components/EditPanel/types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import {
  getThemeCacheKey,
  hasEditableThemeTarget,
  useWidgetThemeParams,
} from '../../../../utils/useWidgetThemeParams';
import { WIDGET_THEME_QUERY_KEY } from '../../../../hooks/useWidgetTheme';
import { updateWidgetConfig as apiUpdateWidgetConfig } from '../../../../api/widgetThemeApi';
import { themeColor } from '../../../../types/color';
import { ConciergeHomeLayoutState, LayoutPreset } from './types';
import { ConciergeContentSkeleton } from './ConciergeSkeletons';
import { LAYOUT_PRESETS, PRESET_LABELS, HOME_CHILD_WIDGET_IDS, FullPresetConfig } from './presets';

const defaultState: ConciergeHomeLayoutState = {
  layoutPreset: 'A1',
  backgroundStyle: 'colored',
  backgroundColor: themeColor('background'),
  elementStyle: 'default',
  logoPosition: 'hero',
  brandLabelPosition: 'hidden',
  showGreeting: false,
  showGreetingIcon: false,
  greetingIconPosition: 'above',
  subtitlePosition: 'hidden',
  startersMode: 'hidden',
  maxStarters: 4,
  cardLayoutDirection: 'stacked',
  footerGap: 8,
  footerPaddingBottom: 0,
  searchInputGap: 32,
  mobile_logoPosition: 'hero',
  mobile_startersMode: 'inherit',
};

const presetOptions = Object.entries(PRESET_LABELS).map(([value, label]) => ({
  label,
  value,
}));

const editFields: EditField[] = [
  // Preset
  {
    key: 'layoutPreset',
    label: 'Layout Preset',
    type: 'options',
    options: presetOptions,
    group: 'Preset',
  },

  // Background
  {
    key: 'backgroundStyle',
    label: 'Background',
    type: 'options',
    options: [
      { label: 'Colored', value: 'colored' },
      { label: 'White', value: 'white' },
    ],
    group: 'Background',
  },
  {
    key: 'backgroundColor',
    label: 'Background Color',
    type: 'color',
    group: 'Background',
    condition: { field: 'backgroundStyle', value: 'colored' },
  },
  {
    key: 'elementStyle',
    label: 'Element Style',
    type: 'options',
    options: [
      { label: 'Default', value: 'default' },
      { label: 'Grey Fill', value: 'grey-fill' },
      { label: 'Grey Fill + Stroke', value: 'grey-fill-stroke' },
    ],
    group: 'Background',
  },

  // Sections
  {
    key: 'logoPosition',
    label: 'Logo Position',
    type: 'options',
    options: [
      { label: 'Hero (center)', value: 'hero' },
      { label: 'Footer (bottom)', value: 'footer' },
      { label: 'After Content', value: 'after-content' },
      { label: 'Hidden', value: 'hidden' },
    ],
    group: 'Sections',
  },
  {
    key: 'brandLabelPosition',
    label: 'Brand Label',
    type: 'options',
    options: [
      { label: 'Above Hero Logo', value: 'above-hero-logo' },
      { label: 'Above Footer Logo', value: 'above-footer-logo' },
      { label: 'Hidden', value: 'hidden' },
    ],
    group: 'Sections',
  },
  {
    key: 'showGreeting',
    label: 'Show Greeting',
    type: 'toggle',
    group: 'Sections',
  },
  {
    key: 'showGreetingIcon',
    label: 'Show Greeting Icon',
    type: 'toggle',
    group: 'Sections',
    condition: 'showGreeting',
  },
  {
    key: 'greetingIconPosition',
    label: 'Icon Position',
    type: 'options',
    options: [
      { label: 'Above Greeting', value: 'above' },
      { label: 'Inline (before text)', value: 'inline' },
    ],
    group: 'Sections',
    condition: 'showGreetingIcon',
  },
  {
    key: 'subtitlePosition',
    label: 'Subtitle Position',
    type: 'options',
    options: [
      { label: 'Under Hero Logo', value: 'under-hero-logo' },
      { label: 'Under Greeting', value: 'under-greeting' },
      { label: 'Hidden', value: 'hidden' },
    ],
    group: 'Sections',
  },
  {
    key: 'startersMode',
    label: 'Starters Layout',
    type: 'options',
    options: [
      { label: 'Hidden', value: 'hidden' },
      { label: 'Pills (text)', value: 'pills' },
      { label: 'Pills (with icons)', value: 'pills-icons' },
      { label: 'Pills + Icons (list)', value: 'pills-icons-list' },
      { label: 'Cards (row)', value: 'cards-row' },
      { label: 'Cards (2x2 grid)', value: 'cards-grid' },
    ],
    group: 'Sections',
  },
  {
    key: 'maxStarters',
    label: 'Max Starters',
    type: 'number',
    min: 1,
    max: 6,
    step: 1,
    group: 'Sections',
    condition: { field: 'startersMode', value: 'hidden', operator: 'not_equals' },
  },
  {
    key: 'cardLayoutDirection',
    label: 'Card Layout',
    type: 'options',
    options: [
      { label: 'Stacked (icon above text)', value: 'stacked' },
      { label: 'Inline (icon beside text)', value: 'inline' },
    ],
    group: 'Sections',
    condition: { field: 'startersMode', value: 'cards', operator: 'includes' },
  },
  {
    key: 'footerGap',
    label: 'Footer / After-Content Gap',
    type: 'number',
    min: 0,
    max: 80,
    step: 4,
    group: 'Sections',
    condition: { field: 'logoPosition', value: ['footer', 'after-content'], operator: 'includes' },
  },
  {
    key: 'footerPaddingBottom',
    label: 'Footer Bottom Padding',
    type: 'number',
    min: 0,
    max: 120,
    step: 4,
    group: 'Sections',
    condition: { field: 'logoPosition', value: 'footer' },
  },
  {
    key: 'searchInputGap',
    label: 'Search Input Gap',
    type: 'number',
    min: 0,
    max: 64,
    step: 4,
    group: 'Sections',
  },

  // Mobile overrides (responsive)
  {
    key: 'mobile_logoPosition',
    label: 'Mobile Logo Position',
    type: 'options',
    options: [
      { label: 'Hero (center)', value: 'hero' },
      { label: 'Header (top bar)', value: 'header' },
      { label: 'Footer (bottom)', value: 'footer' },
      { label: 'Hidden', value: 'hidden' },
      { label: 'Inherit from Desktop', value: 'inherit' },
    ],
    group: 'Mobile',
  },
  {
    key: 'mobile_startersMode',
    label: 'Mobile Starters Layout',
    type: 'options',
    options: [
      { label: 'Inherit from Desktop', value: 'inherit' },
      { label: 'Hidden', value: 'hidden' },
      { label: 'Pills (text)', value: 'pills' },
      { label: 'Pills (with icons)', value: 'pills-icons' },
      { label: 'Pills + Icons (list)', value: 'pills-icons-list' },
      { label: 'Cards (row)', value: 'cards-row' },
      { label: 'Cards (2x2 grid)', value: 'cards-grid' },
    ],
    group: 'Mobile',
  },
];

export interface ConciergeHomeLayoutWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeLayoutState>;
  children: (state: ConciergeHomeLayoutState, resolveColor: (color: any) => string) => React.ReactNode;
}

export const ConciergeHomeLayoutWidget: React.FC<ConciergeHomeLayoutWidgetProps> = ({
  widgetId = 'concierge-home-layout',
  displayName = 'Home Layout',
  initialState = {},
  children,
}) => {
  const queryClient = useQueryClient();
  const themeParams = useWidgetThemeParams();
  const dirtyChildWidgetIds = useRef<Set<string>>(new Set());

  const {
    state,
    isLoading,
    isSaving,
    handleEditChange: rawHandleEditChange,
    handleSave: rawHandleSave,
    resetField,
    savedKeys,
    themeColors,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHomeLayoutState>(widgetId, { ...defaultState, ...initialState }, editFields);

  const applyChildWidgetConfigs = useCallback(
    (preset: FullPresetConfig) => {
      const overrides = preset.childWidgets ?? {};

      HOME_CHILD_WIDGET_IDS.forEach((id) => dirtyChildWidgetIds.current.add(id));

      queryClient.setQueryData([...WIDGET_THEME_QUERY_KEY, getThemeCacheKey(themeParams)], (oldData: any) => {
        if (!oldData) return oldData;

        const updatedWidgetConfig = {
          ...oldData?.config?.widgetConfig,
        };

        for (const childId of HOME_CHILD_WIDGET_IDS) {
          if (childId in overrides) {
            updatedWidgetConfig[childId] = {
              ...overrides[childId],
            };
          } else {
            // Reset to component defaults by removing saved config
            delete updatedWidgetConfig[childId];
          }
        }

        return {
          ...oldData,
          config: {
            ...oldData?.config,
            widgetConfig: updatedWidgetConfig,
          },
        };
      });
    },
    [queryClient, themeParams],
  );

  const handleSave = useCallback(async () => {
    await rawHandleSave();

    if (dirtyChildWidgetIds.current.size > 0 && hasEditableThemeTarget(themeParams) && themeParams.editThemeToken) {
      const theme = queryClient.getQueryData([...WIDGET_THEME_QUERY_KEY, getThemeCacheKey(themeParams)]) as any;

      const savePromises = Array.from(dirtyChildWidgetIds.current).map((childId) => {
        const widgetState = theme?.config?.widgetConfig?.[childId];
        // Save empty object for widgets that were reset (deleted from cache)
        return apiUpdateWidgetConfig({
          themeId: themeParams.themeId || '',
          widgetId: childId,
          widgetState: widgetState ?? {},
          editThemeToken: themeParams.editThemeToken!,
        });
      });

      try {
        await Promise.all(savePromises);
        dirtyChildWidgetIds.current.clear();
      } catch (error: any) {
        console.error('Failed to save child widget configs:', error);
      }
    }
  }, [rawHandleSave, queryClient, themeParams]);

  // Intercept preset changes to apply preset values
  const handleEditChange = useCallback(
    (key: string, value: any) => {
      if (key === 'layoutPreset' && value !== 'custom') {
        const preset = LAYOUT_PRESETS[value as Exclude<LayoutPreset, 'custom'>];
        if (preset) {
          Object.entries(preset.layout).forEach(([k, v]) => {
            rawHandleEditChange(k, v);
          });
          applyChildWidgetConfigs(preset);
        }
      }
      rawHandleEditChange(key, value);
    },
    [rawHandleEditChange, applyChildWidgetConfigs],
  );

  if (isLoading) {
    return <ConciergeContentSkeleton />;
  }

  const widgetContent = children(state, resolveColor);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      triggerMode="persistent"
      triggerIcon="gear"
      triggerPosition="bottom-right"
      triggerLabel="Edit Home Layout"
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
